import { Popover, type PopoverProps } from "antd";
import { CSSProperties, PropsWithChildren } from "react";
import styles from "./IndicatorBadge.module.scss";
type IndicatorBadgeProps = PropsWithChildren & {
  label?: string | React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  variant?: "default" | "outline";
  placement?: PopoverProps["placement"];
  offset?: [number, number]; // [x,y] axis
  zIndex?: number;
  size?: "default" | "large";
  mode?: "popover" | "inline";
};

// for overlap
const getInlinePositionStyle = (placement: PopoverProps["placement"], [x, y]: [number, number]): CSSProperties => {
  switch (placement) {
    case "left":
      return {
        right: "100%",
        top: "50%",
        transform: `translate(${x}px, calc(-50% + ${y}px))`
      };
    case "top":
      return {
        left: "50%",
        bottom: "100%",
        transform: `translate(calc(-50% + ${x}px), ${y}px)`
      };
    case "topLeft":
      return {
        left: 0,
        bottom: "100%",
        transform: `translate(${x}px, ${y}px)`
      };
    case "topRight":
      return {
        top: 0,
        right: 0,
        transform: `translate(${x}px, ${y}px)`
      };
    case "right":
    default:
      return {
        left: "100%",
        top: "50%",
        transform: `translate(${x}px, calc(-50% + ${y}px))`
      };
  }
};
const IndicatorBadge = ({
  label = "New",
  icon,
  className = "",
  children,
  variant = "default",
  placement = "right",
  offset = [0, -14],
  zIndex = 999,
  size = "default",
  mode = "inline"
}: IndicatorBadgeProps) => {
  const variantClass = variant === "outline" ? "bg-surface/80 text-red-600 border-red-600" : "bg-red-600 border-transparent text-white";
  const sizeClass = size === "large" ? "text-sm px-2 leading-5" : "text-[11px] px-1 leading-4";
  const badgeContent = icon ? <div className="relative inline-flex">
      <span className={`relative z-10 inline-flex justify-center text-sm text-red-600 ${styles.scale}`}>{icon}</span>
    </div> : <div className="relative inline-flex">
      <span className={`absolute inset-0 z-0 m-auto h-2 w-4 rounded-full ${styles.pulse} `} />
      <span className={`relative z-10 inline-flex items-center justify-center rounded-full border ${variantClass} ${styles.scale} ${sizeClass}`}>
        {label}
      </span>
    </div>;
  if (mode === "inline") {
    return <span className="relative inline-flex">
        {children}
        <span className={`pointer-events-none absolute ${className}`} style={{
        zIndex,
        ...getInlinePositionStyle(placement, offset)
      }}>
          {badgeContent}
        </span>
      </span>;
  }
  return <Popover open arrow={false} placement={placement} align={{
    offset
  }} zIndex={zIndex} classNames={{
    root: "!pointer-events-none !p-0 !m-0",
    container: "!p-0 !m-0",
    content: "!p-0 !m-0 !bg-transparent !shadow-none flex"
  }} content={badgeContent} data-sentry-element="Popover" data-sentry-component="IndicatorBadge" data-sentry-source-file="IndicatorBadge.tsx">
      <span className={`inline-flex ${className}`}>{children}</span>
    </Popover>;
};
export default IndicatorBadge;